import { format } from "date-fns"
import { GripVertical, Package, X } from "lucide-react"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { layoutOffsetX } from "@/lib/pointerCoords"
import type { SegmentLoadState } from "@/lib/routeCostCalculation"
import { getRouteKindStopClasses } from "@/lib/routeKindDisplay"
import { getStopTypeColor, getStopTypeIcon } from "@/lib/slotTypeDisplay"
import { deriveStopPresentation, legSpan } from "@/lib/stopPresentation"
import { timeToPosition } from "@/lib/timelineScale"
import { cn } from "@/lib/utils"
import type { Route, Stop } from "@/types/collections"

import StopTooltipContent from "./StopTooltipContent"
import type { DragType } from "./useStopDrag"

interface TimelineStopProps {
  stop: Stop
  route: Route
  index: number
  /** From segmentLoadStates over the same stops; none for the first. */
  legState: SegmentLoadState | undefined
  isPastDate: boolean
  currentTime: Date
  selectedDate: Date
  hourWidth: number
  isBeingDragged: boolean
  /** Some stop is being dragged, so a click is the end of a drag. */
  isDragging: boolean
  routeHasUnsavedChanges: boolean
  onDragStart: (e: React.MouseEvent, stop: Stop, route: Route, type: DragType) => void
  onClick: (stop: Stop, route: Route) => void
  onDelete: (stop: Stop, route: Route) => void
}

export default function TimelineStop({
  stop,
  route,
  index,
  legState,
  isPastDate,
  currentTime,
  selectedDate,
  hourWidth,
  isBeingDragged,
  isDragging,
  routeHasUnsavedChanges,
  onDragStart,
  onClick,
  onDelete,
}: TimelineStopProps) {
  const toPosition = (time: Date) => timeToPosition(time, selectedDate, hourWidth)
  const presentation = deriveStopPresentation(stop, route, {
    index,
    isPastDate,
    currentTime,
    selectedDate,
    hourWidth,
  })
  const {
    isSkipped,
    hasDeparted,
    unaccounted,
    settled,
    isNextStop,
    isEditableStop,
    arrivalTime,
    startPos,
    width,
    durationOnSite,
    hasActual,
    timeUnrecorded,
    etaDiffers,
    calculatedDelayMinutes,
    actualDelayMinutes,
    pastArrivalRing,
  } = presentation

  return (
    <>
      {index > 0 && (
        <StopLeg
          prevStop={route.stops[index - 1]}
          stop={stop}
          route={route}
          legState={legState}
          isPastDate={isPastDate}
          isEditableStop={isEditableStop}
          toPosition={toPosition}
        />
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "group absolute bottom-1.5 top-1.5 z-10 rounded border border-l-4 border-palette-jungleGreen200 shadow-sm transition-all",
              // Stand-by stops are held time whatever their type. Replaces the type colour: twMerge
              // can't resolve a plain class against bg-*.
              route.routeKind === "standby"
                ? getRouteKindStopClasses("standby")
                : getStopTypeColor(stop.type),
              // On a finished route, a stop with no record is not upcoming.
              settled && "bg-palette-jungleGreen100 opacity-60",
              // Dashed for Samsara asserting a miss, or nothing recorded at all.
              (isSkipped || unaccounted) &&
                "border-dashed border-palette-jungleGreen400",
              pastArrivalRing === "late" &&
                "border-red-300 bg-red-50/50 ring-2 ring-red-500 ring-offset-1",
              pastArrivalRing === "early" &&
                "border-green-300 bg-green-50/50 ring-2 ring-green-500 ring-offset-1",
              // Past dates can be viewed even when unlocked.
              (route.isLocked || isPastDate) &&
                "cursor-pointer hover:z-20 hover:scale-105",
              !route.isLocked &&
                !settled &&
                !isPastDate &&
                "cursor-grab hover:z-20 hover:ring-2 hover:ring-blue-400 hover:ring-offset-1",
              !route.isLocked && settled && !isPastDate && "cursor-not-allowed",
              isBeingDragged && "z-30 scale-105 shadow-lg ring-2 ring-blue-500",
              routeHasUnsavedChanges &&
                !route.isLocked &&
                !settled &&
                "ring-1 ring-amber-300"
            )}
            style={{
              left: `${startPos}px`,
              width: `${width}px`,
              minWidth: "30px",
            }}
            onClick={() => !isDragging && onClick(stop, route)}
            onMouseDown={(e) => {
              e.stopPropagation() // Prevent the row's drag-to-create.
              const target = e.currentTarget
              const relativeX = layoutOffsetX(target, e.clientX)
              const stopWidth = target.offsetWidth
              const handleWidth = 8

              if (!route.isLocked && !settled) {
                if (relativeX <= handleWidth) {
                  onDragStart(e, stop, route, "resize-left")
                } else if (relativeX >= stopWidth - handleWidth) {
                  onDragStart(e, stop, route, "resize-right")
                } else {
                  onDragStart(e, stop, route, "move")
                }
              }
            }}
          >
            {!route.isLocked && !settled && (
              <div
                className="absolute bottom-0 left-0 top-0 w-2 cursor-ew-resize rounded-l bg-blue-500/20 opacity-0 transition-opacity group-hover:opacity-100"
                onMouseDown={(e) => {
                  e.stopPropagation()
                  onDragStart(e, stop, route, "resize-left")
                }}
              />
            )}

            {!route.isLocked && !settled && (
              <div
                className="absolute bottom-0 right-0 top-0 w-2 cursor-ew-resize rounded-r bg-blue-500/20 opacity-0 transition-opacity group-hover:opacity-100"
                onMouseDown={(e) => {
                  e.stopPropagation()
                  onDragStart(e, stop, route, "resize-right")
                }}
              />
            )}

            <div className="flex h-full flex-col justify-between overflow-hidden p-1">
              <div className="flex items-center gap-0.5">
                <span className="text-xs">{getStopTypeIcon(stop.type)}</span>
                {width > 50 && (
                  <span className="truncate text-[9px] font-medium text-palette-jungleGreen900">
                    {stop.name}
                  </span>
                )}
              </div>

              {width > 40 && (
                <div className="text-[8px] text-palette-jungleGreen500">
                  {isSkipped ? (
                    <span className="font-medium italic text-orange-600">
                      Skipped
                    </span>
                  ) : hasDeparted && durationOnSite !== null ? (
                    <span>
                      {Math.floor(durationOnSite / 60)}h {durationOnSite % 60}m
                    </span>
                  ) : hasActual ? (
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-emerald-600">
                        {format(stop.actualArrival, "HH:mm")}
                      </span>
                      {isPastDate && (
                        <>
                          <span className="text-[7px] text-palette-jungleGreen400 line-through">
                            {format(stop.plannedArrival, "HH:mm")}
                          </span>
                          {actualDelayMinutes !== null &&
                            actualDelayMinutes !== 0 && (
                              <span
                                className={cn(
                                  "text-[7px] font-bold",
                                  actualDelayMinutes > 0
                                    ? "text-red-600"
                                    : "text-green-600"
                                )}
                              >
                                ({actualDelayMinutes > 0 ? "+" : ""}
                                {actualDelayMinutes}m)
                              </span>
                            )}
                        </>
                      )}
                    </div>
                  ) : timeUnrecorded ? (
                    <span
                      className="font-medium text-palette-jungleGreen400"
                      title={
                        unaccounted
                          ? "This route has finished and nothing was ever recorded for this stop, by the vehicle's GPS or by the driver's app. Worth chasing."
                          : "No arrival time was recorded for this stop, by the vehicle's GPS or by the driver's app. The stops either side show the vehicle passed through."
                      }
                    >
                      &mdash;
                    </span>
                  ) : (
                    <>
                      <span
                        className={cn(isNextStop && "font-medium text-blue-600")}
                      >
                        {format(arrivalTime, "HH:mm")}
                      </span>
                      {etaDiffers && (
                        <span className="ml-0.5 text-[7px] text-palette-jungleGreen400">
                          (plan: {format(stop.plannedArrival, "HH:mm")})
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}

              {width > 60 && (
                <div className="flex items-center gap-1 text-[8px] text-palette-jungleGreen500">
                  <Package size={8} />
                  <span>{stop.volumeActual ?? stop.volumeEstimated}</span>
                  {stop.volumeActual != null && (
                    <span className="font-medium text-emerald-600">(actual)</span>
                  )}
                </div>
              )}
            </div>

            {isPastDate &&
              stop.actualArrival &&
              actualDelayMinutes !== null &&
              Math.abs(actualDelayMinutes) >= 5 && (
                <div
                  className={cn(
                    "absolute -right-2 -top-2 z-10 rounded-full border border-white px-1.5 py-0.5 text-[8px] font-bold shadow-md",
                    actualDelayMinutes > 0
                      ? "bg-red-500 text-white"
                      : "bg-green-600 text-white"
                  )}
                >
                  {actualDelayMinutes > 0 ? "+" : ""}
                  {actualDelayMinutes}m
                </div>
              )}
            {!isPastDate &&
              !settled &&
              calculatedDelayMinutes !== undefined &&
              calculatedDelayMinutes !== null &&
              calculatedDelayMinutes >= 5 && (
                <div
                  className={cn(
                    "absolute -right-2 -top-2 z-10 rounded-full border border-white px-1.5 py-0.5 text-[8px] font-bold shadow-md",
                    "bg-red-500 text-white"
                  )}
                >
                  +{calculatedDelayMinutes}m
                </div>
              )}

            {!route.isLocked && !settled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(stop, route)
                    }}
                    className="absolute -right-2 -top-2 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white opacity-0 shadow-md transition-opacity hover:bg-red-600 group-hover:opacity-100"
                  >
                    <X size={10} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  Cancel stop
                </TooltipContent>
              </Tooltip>
            )}

            {!route.isLocked && !settled && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                <div className="rounded bg-white/80 px-1 py-0.5 shadow-sm">
                  <GripVertical size={10} className="text-palette-jungleGreen500" />
                </div>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <StopTooltipContent
          stop={stop}
          route={route}
          presentation={presentation}
          isPastDate={isPastDate}
          currentTime={currentTime}
        />
      </Tooltip>
    </>
  )
}

function StopLeg({
  prevStop,
  stop,
  route,
  legState,
  isPastDate,
  isEditableStop,
  toPosition,
}: {
  prevStop: Stop
  stop: Stop
  route: Route
  legState: SegmentLoadState | undefined
  isPastDate: boolean
  isEditableStop: boolean
  toPosition: (time: Date) => number
}) {
  const leg = legSpan(prevStop, stop, route, isPastDate, isEditableStop)
  const prevEndPos = toPosition(leg.from)
  const currStartPos = toPosition(leg.to)
  const segmentWidth = currStartPos - prevEndPos

  if (segmentWidth <= 0) {
    return null
  }

  if (legState === "internal") {
    return (
      <div
        className="absolute top-1/2 z-0 h-0 -translate-y-1/2 border-t-[4px] border-dashed border-palette-jungleGreen400"
        style={{
          left: `${prevEndPos}px`,
          width: `${segmentWidth}px`,
        }}
      />
    )
  }
  const isEmptyLeg = legState === "empty"
  return (
    <div
      className={cn(
        "absolute top-1/2 z-0 h-1 -translate-y-1/2 rounded-full",
        isEmptyLeg ? "bg-red-200" : "bg-palette-jungleGreen300"
      )}
      style={{
        left: `${prevEndPos}px`,
        width: `${segmentWidth}px`,
      }}
    />
  )
}
